import AppBar from "@mui/material/AppBar";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { useCallback } from "react";
import { ui } from "../../../interfaces/interfaces";
import useDebugInformation from "../../../hooks/useDebugInformation";
import { useCarePlanTabs } from "../../../hooks/useCarePlanTabs";
import { getIcon, a11yProps, getFormValidationState } from "./helpers";

const name = "CarePlanTabHeader";

/**
 * Component for rendering the header tabs in the Care Plan interface.
 *
 * @param {Object} props - Component props.
 * @param {string} props.parent - The parent component name.
 * @param {string} props.affix - A fixed string denoting a specific part of the interface.
 * @returns {JSX.Element} - The rendered AppBar component with tabs.
 */
const CarePlanTabHeader: React.FC<CarePlanTabHeaderProps> = ({ parent, affix }) => {
  const { isPaymentEnabled, serviceAgreementActiveTab, serviceAgreementTabsList, isCustomerValid, isPlanDetailsValid, isBillingValid, isPaymentValid } = useCarePlanTabs();

  const debugName = `${parent}▷[${name}]`;
  useDebugInformation(debugName, { serviceAgreementActiveTab, serviceAgreementTabsList, affix, isPaymentEnabled, isCustomerValid, isPlanDetailsValid, isBillingValid, isPaymentValid });

  // Optimize handleChange function with useCallback to prevent unnecessary re-renders.
  const handleChange = useCallback((event: React.SyntheticEvent, value: number) => {
    ui.CarePlanActiveTab = value;
  }, []);

  return (
    <AppBar position="static" color="default">
      <Tabs
        value={serviceAgreementActiveTab}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        aria-label="action tabs example"
        sx={{ minHeight: "unset" }}
      >
        {serviceAgreementTabsList.map((tab, idx) => (
          <Tab
            key={idx}
            label={tab.label}
            disabled={idx === 3 ? !isPaymentEnabled : false}
            {...a11yProps(idx)}
            icon={getIcon(idx, getFormValidationState(idx, [isPlanDetailsValid, isCustomerValid, isBillingValid, isPaymentValid]))}
            iconPosition="start"
            sx={{ minHeight: "unset" }}
          />
        ))}
      </Tabs>
    </AppBar>
  );
};

CarePlanTabHeader.displayName = name;


export default React.memo(CarePlanTabHeader);

type CarePlanTabHeaderProps = {
  parent: string;
  affix: "ServiceAgreement";
};
